import { useEffect, useRef, useState } from 'react';

/**
 * Hook personalizado para animaciones de scroll reveal usando Intersection Observer
 * Optimizado para rendimiento con GPU-accelerated transforms
 * 
 * @param {Object} options - Opciones de configuración
 * @param {number} options.threshold - Porcentaje de visibilidad requerido (0-1)
 * @param {string} options.rootMargin - Margen del root para trigger anticipado
 * @param {boolean} options.triggerOnce - Si solo debe animarse una vez
 * @param {number} options.delay - Delay en ms antes de aplicar la animación
 * @returns {[React.RefObject, boolean]} - [ref, isVisible]
 */
export const useScrollReveal = ({
  threshold = 0.1,
  rootMargin = '0px 0px -100px 0px',
  triggerOnce = true,
  delay = 0
} = {}) => {
  const [isVisible, setIsVisible] = useState(false);
  const elementRef = useRef(null);
  const hasTriggeredRef = useRef(false);

  useEffect(() => {
    const element = elementRef.current;
    if (!element) return;

    // Si ya se activó y triggerOnce es true, no hacer nada
    if (hasTriggeredRef.current && triggerOnce) return;

    // Crear Intersection Observer
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // Aplicar delay si está configurado
            if (delay > 0) {
              setTimeout(() => {
                setIsVisible(true);
                hasTriggeredRef.current = true;
              }, delay);
            } else {
              setIsVisible(true);
              hasTriggeredRef.current = true;
            }

            // Desconectar si solo debe activarse una vez
            if (triggerOnce) {
              observer.disconnect();
            }
          } else if (!triggerOnce) {
            // Si no es triggerOnce, resetear cuando sale del viewport
            setIsVisible(false);
          }
        });
      },
      {
        threshold,
        rootMargin
      }
    );

    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, [threshold, rootMargin, triggerOnce, delay]);

  return [elementRef, isVisible];
};


